/**
 * Infinite Scroll Component
 * 
 * Auto-load more content on scroll
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import './InfiniteScroll.css'

interface InfiniteScrollProps {
  children: React.ReactNode
  hasMore: boolean
  isLoading: boolean
  onLoadMore: () => void
  threshold?: number // Distance from bottom to trigger load (px)
  loader?: React.ReactNode
  endMessage?: React.ReactNode
  className?: string
}

const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  children,
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 200,
  loader,
  endMessage,
  className = ''
}) => {
  const [showEndMessage, setShowEndMessage] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0]
      if (target.isIntersecting && hasMore && !isLoading) {
        onLoadMore()
      }
    },
    [hasMore, isLoading, onLoadMore]
  )

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: `${threshold}px`,
      threshold: 0
    })

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [handleObserver, threshold])

  // Show end message when no more items
  useEffect(() => {
    if (!hasMore && !isLoading) {
      const timer = setTimeout(() => setShowEndMessage(true), 500)
      return () => clearTimeout(timer)
    }
    setShowEndMessage(false)
  }, [hasMore, isLoading])

  return (
    <div className={`infinite-scroll-container ${className}`}>
      {/* Content */}
      <div className="infinite-scroll-content">
        {children}
      </div>

      {/* Sentinel for intersection observer */}
      <div ref={sentinelRef} className="infinite-scroll-sentinel" />

      {/* Loading Indicator */}
      {isLoading && (
        <div className="infinite-scroll-loader">
          {loader || (
            <div className="default-loader">
              <div className="loader-dots">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
              <span className="loader-text">Loading more...</span>
            </div>
          )}
        </div>
      )}

      {/* End Message */}
      {showEndMessage && endMessage && (
        <div className="infinite-scroll-end">
          {endMessage}
        </div>
      )}
    </div>
  )
}

// Custom hook for managing infinite scroll state
export function useInfiniteScroll<T>({
  initialPage = 1,
  perPage = 20,
  threshold = 200
}: {
  initialPage?: number
  perPage?: number
  threshold?: number
}) {
  const [items, setItems] = useState<T[]>([])
  const [page, setPage] = useState(initialPage)
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const loadMore = useCallback(async (fetchFn: (page: number, perPage: number) => Promise<T[]>) => {
    if (isLoading || !hasMore) return

    setIsLoading(true)
    setError(null)

    try {
      const newItems = await fetchFn(page, perPage)
      
      if (newItems.length < perPage) {
        setHasMore(false)
      }
      
      setItems(prev => [...prev, ...newItems])
      setPage(prev => prev + 1)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load'))
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, hasMore, page, perPage])

  const reset = useCallback(() => {
    setItems([])
    setPage(initialPage)
    setHasMore(true)
    setError(null)
  }, [initialPage])

  return {
    items,
    page,
    hasMore,
    isLoading,
    error,
    loadMore,
    reset,
    setItems
  }
}

export default InfiniteScroll